import { useWorkspaceText } from '../interface-copy';
import { useEffect, useState } from 'react';
import { useCompany } from '@/company/context';
import { useAgentFeatures } from '../agent-features';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { subscribeKnowledgePagesChanged } from '../knowledge-agent-events';
import { errorStatus, knowledgePagesApi } from './api';
import { pageTarget } from './format';
import { suggestionPointer } from './suggestion-link';
import type { PageProposal, PageProposalKind } from './types';

type Status = 'open' | 'outdated' | 'approved' | 'rejected' | 'withdrawn' | 'superseded';

type Live =
  | { state: 'loading' }
  | { state: 'ready'; proposal: PageProposal; status: Status }
  | { state: 'missing' }
  | { state: 'unknown'; proposal: PageProposal | null; status: Status | null };

/**
 * A pending suggestion is open until the page moves on under it (its quoted
 * text changed, or a newer revision for an archive); the page view says which.
 */
async function readStatus(proposal: PageProposal): Promise<Status> {
  if (proposal.status !== 'pending') return proposal.status;
  if (proposal.kind === 'create_page' || !proposal.pageId) return 'open';
  const view = await knowledgePagesApi.view(proposal.pageId);
  return view.suggestions.find((item) => item.id === proposal.id)?.state === 'outdated'
    ? 'outdated'
    : 'open';
}

function useLiveSuggestion(id: string | null, enabled: boolean) {
  const companyId = useCompany().data?.company?.id ?? '';
  const key = id && companyId ? `${companyId}\0${id}` : null;
  const [live, setLive] = useState<{ key: string | null; value: Live }>({
    key,
    value: { state: 'loading' },
  });
  const [reload, setReload] = useState(0);
  useEffect(() => subscribeKnowledgePagesChanged(() => setReload((value) => value + 1)), []);
  useEffect(() => {
    if (!key || !enabled) return;
    let active = true;
    void knowledgePagesApi
      .proposal(id!)
      .then(async (proposal) => ({ proposal, status: await readStatus(proposal) }))
      .then(
        ({ proposal, status }) => {
          if (active) setLive({ key, value: { state: 'ready', proposal, status } });
        },
        (failure) => {
          if (!active) return;
          const code = errorStatus(failure);
          setLive((previous) => {
            if (code === 404 || code === 403) return { key, value: { state: 'missing' } };
            const last = previous.key === key ? previous.value : null;
            // A decided suggestion never changes again, so its last status stays.
            if (last?.state === 'ready' && last.status !== 'open' && last.status !== 'outdated')
              return previous;
            return {
              key,
              value: {
                state: 'unknown',
                proposal:
                  last?.state === 'ready' || last?.state === 'unknown' ? last.proposal : null,
                status: null,
              },
            };
          });
        },
      );
    return () => {
      active = false;
    };
  }, [key, enabled, reload]);
  return {
    value: live.key === key ? live.value : ({ state: 'loading' } as Live),
    retry: () => setReload((value) => value + 1),
  };
}

/**
 * A suggested page change in the transcript, with its live status. Deciding
 * happens on the page: "Show on page" opens it beside the chat with this
 * suggestion in focus.
 */
export function PageProposalChatCard({ output }: { output: Record<string, unknown> }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const statusLabel: Record<Status, string> = {
    open: uiText('Waiting for review'),
    outdated: uiText('Outdated'),
    approved: uiText('Accepted'),
    rejected: uiText('Rejected'),
    withdrawn: uiText('Withdrawn'),
    superseded: uiText('Replaced'),
  };
  const kindLabel: Record<PageProposalKind, string> = {
    update_page: uiText('Suggested edit'),
    create_page: uiText('Suggested new page'),
    move_page: uiText('Suggested move'),
    archive_page: uiText('Suggested archive'),
  };
  const features = useAgentFeatures();
  const pointer = suggestionPointer(output);
  const enabled = features?.knowledgePages === true;
  const { value: live, retry } = useLiveSuggestion(pointer?.id ?? null, enabled);
  if (!pointer)
    return (
      <div className="ws-error" role="alert">
        {uiText('This suggested knowledge update could not be shown.')}
      </div>
    );
  const proposal = live.state === 'ready' || live.state === 'unknown' ? live.proposal : null;
  const status =
    live.state === 'ready' ? live.status : live.state === 'unknown' ? live.status : null;
  const title = proposal ? (proposal.pageTitle ?? proposal.title) : uiText('Knowledge page');
  const label =
    live.state === 'loading'
      ? uiText('Checking status…')
      : live.state === 'missing'
        ? uiText('Not available')
        : status
          ? statusLabel[status]
          : uiText('Status unavailable');
  const open = () => {
    const pageId =
      proposal?.kind === 'create_page' && proposal.status === 'pending'
        ? proposal.parentId
        : (proposal?.pageId ?? pointer.pageId);
    ws.openCanvas(
      pageId
        ? pageTarget({ id: pageId, suggestion: pointer.id })
        : { kind: 'page-proposal', id: pointer.id },
    );
  };
  return (
    <section
      className="ws-artifact-card ws-page-card"
      aria-label={`${proposal ? kindLabel[proposal.kind] : uiText('Suggested knowledge update')} · ${title}`}
    >
      <WorkspaceIcon name="knowledge" size={20} />
      <span className="ws-page-card-copy">
        <small className="ws-page-card-eyebrow">
          {proposal ? kindLabel[proposal.kind] : uiText('Suggested knowledge update')}
        </small>
        <strong>{title}</strong>
        {proposal?.reason && <small className="ws-page-card-reason">{proposal.reason}</small>}
        {features && !enabled && (
          <small>{uiText('Knowledge pages are not enabled in this workspace.')}</small>
        )}
        {live.state === 'missing' && (
          <small>{uiText('This suggestion is no longer available to you.')}</small>
        )}
        {live.state === 'unknown' && (
          <small>
            {uiText('We could not check where this suggestion stands.')}{' '}
            <button type="button" className="ws-text-button" onClick={retry}>
              {uiText('Check again')}
            </button>
          </small>
        )}
      </span>
      <span className="ws-page-card-side">
        {enabled && (
          <span className="ws-review-status" data-status={status ?? live.state}>
            {label}
          </span>
        )}
        {enabled && live.state !== 'missing' && (
          <button type="button" className="ws-button" onClick={open}>
            {uiText('Show on page')}
          </button>
        )}
      </span>
    </section>
  );
}
